/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { DesktopRouteState } from '@poweralpha/common';
import {
  dropOnTiles,
  findDropTarget,
  layoutTiles,
  removeLeaf,
  type TileDropTarget,
  type TileNode,
  tiledWindowIds,
  tileLeaves,
  tilesFit,
  vacateLeaf,
} from './tileTree';
import { getMinWindowSize, MIN_FIT_WIDTH } from './windowDefaults';
import type { WindowSnap } from './windowInteractions';
import type { DesktopState, DesktopWindowState } from './windowSystem';

export interface DesktopBounds {
  width: number;
  height: number;
}

/** The window layer in its own coordinates; a default until it has been measured. */
export const getDesktopRect = (availableBounds: DesktopBounds | undefined) => ({
  x: 0,
  y: 0,
  ...(availableBounds ?? { width: 1200, height: 800 }),
});

export const isTiledWindow = (state: Pick<DesktopState, 'tileLayout'>, id: string) =>
  tiledWindowIds(state.tileLayout).includes(id);

/** Leaf and seam rects of the current tiles, in window-layer coordinates. */
export const getTileLayout = ({
  tileLayout,
  availableBounds,
}: Pick<DesktopState, 'tileLayout' | 'availableBounds'>) =>
  layoutTiles(tileLayout, getDesktopRect(availableBounds));

/**
 * Where the tiles sit in the window stack: their dividers share the top tile's level (so windows
 * floating above the tiles cover them too), and go away while a full-screen window covers them.
 */
export const getTileStacking = ({
  tileLayout,
  windows,
}: Pick<DesktopState, 'tileLayout' | 'windows'>) => {
  const ids = new Set(tiledWindowIds(tileLayout));
  const zIndex = Math.max(0, ...windows.filter(({ id }) => ids.has(id)).map((w) => w.zIndex));
  const covered = windows.some(
    (window) => isShown(window) && window.isMaximized && window.zIndex > zIndex,
  );
  return { zIndex, covered };
};

/** Snap targets for dragging window `id`, from the tiles as they are when the drag starts. */
export function createWindowSnap(state: DesktopState, id: string): WindowSnap {
  const bounds = getDesktopRect(state.availableBounds);
  const { leaves } = layoutTiles(state.tileLayout, bounds);
  return {
    findTarget: (point) => {
      const target = findDropTarget({ point, bounds, leaves, draggedId: id });
      return target && canDropWindow(state, id, target) ? target : null;
    },
    previewRect: (target) =>
      target.kind === 'maximize'
        ? bounds
        : layoutTiles(dropOnTiles(state.tileLayout, target, id), bounds).leaves.get(id),
  };
}

/** A snap that would squeeze any tile below the minimum size isn't offered (or applied). */
export const canDropWindow = (
  state: Pick<DesktopState, 'tileLayout' | 'availableBounds'>,
  id: string,
  target: TileDropTarget,
) =>
  target.kind === 'maximize' ||
  tilesFit(dropOnTiles(state.tileLayout, target, id), getDesktopRect(state.availableBounds));

const isShown = (window: DesktopWindowState) => window.isOpen && !window.isMinimized;

const focus = <T extends DesktopRouteState>(
  state: DesktopState<T>,
  id: string,
  updates: Partial<DesktopWindowState<T>> = {},
): DesktopState<T> => ({
  ...state,
  focusedWindowId: id,
  nextZIndex: state.nextZIndex + 1,
  windows: state.windows.map((window) =>
    window.id === id
      ? { ...window, ...updates, zIndex: state.nextZIndex, updatedAt: new Date().toISOString() }
      : window,
  ),
});

/** Takes window `id` out of the tiles; it returns to its own size unless `keepBounds`. */
export function untile<T extends DesktopRouteState>(
  state: DesktopState<T>,
  id: string,
  { keepBounds = false, keepSlot = false }: { keepBounds?: boolean; keepSlot?: boolean } = {},
): DesktopState<T> {
  if (!isTiledWindow(state, id)) return state;
  return {
    ...state,
    tileLayout: keepSlot ? vacateLeaf(state.tileLayout, id) : removeLeaf(state.tileLayout, id),
    windows: keepBounds
      ? state.windows.map((window) =>
          window.id === id ? { ...window, restoreBounds: undefined } : window,
        )
      : state.windows,
  };
}

/**
 * Green traffic light: a windowed or tiled window goes full screen (other tiles stay); a
 * full-screen window becomes a centered window a quarter of the desktop, or the app's minimum
 * size when that is larger. Tiling is up to the user from there (drag to an edge).
 */
export function toggleFullScreen<T extends DesktopRouteState>(
  state: DesktopState<T>,
  id: string,
): DesktopState<T> {
  const target = state.windows.find((window) => window.id === id);
  if (!target || !isShown(target)) return state;
  if (!target.isMaximized)
    return focus(reflowTiles(untile(state, id, { keepSlot: true })), id, { isMaximized: true });
  const desktop = getDesktopRect(state.availableBounds);
  const min = getMinWindowSize(target.appId);
  const width = Math.min(desktop.width, Math.max(min.width, Math.round(desktop.width / 2)));
  const height = Math.min(desktop.height, Math.max(min.height, Math.round(desktop.height / 2)));
  return focus(state, id, {
    isMaximized: false,
    width,
    height,
    x: Math.round((desktop.width - width) / 2),
    y: Math.round((desktop.height - height) / 2),
  });
}

/** Fills the offered slot (or the first free one) with an open app. */
export function addTile<T extends DesktopRouteState>(
  state: DesktopState<T>,
  id: string,
): DesktopState<T> {
  const slotId =
    state.tilePickerSlotId ?? tileLeaves(state.tileLayout).find(({ kind }) => kind === 'slot')?.id;
  if (
    !slotId ||
    isTiledWindow(state, id) ||
    !state.windows.some((window) => window.id === id && isShown(window))
  )
    return state;
  return focus(
    {
      ...state,
      tileLayout: dropOnTiles(state.tileLayout, { kind: 'leaf', id: slotId, side: 'center' }, id),
      tilePickerSlotId: undefined,
    },
    id,
  );
}

/** A dragged window released over a snap target. New free space offers the open apps. */
export function dropWindow<T extends DesktopRouteState>(
  state: DesktopState<T>,
  id: string,
  target: TileDropTarget,
): DesktopState<T> {
  if (target.kind === 'maximize')
    return focus(reflowTiles(untile(state, id, { keepSlot: true })), id, { isMaximized: true });
  if (target.kind === 'leaf' && target.id === id) return state;
  const before = new Set(tileLeaves(state.tileLayout).map((leaf) => leaf.id));
  const tileLayout = dropOnTiles(state.tileLayout, target, id);
  const newSlot = tileLeaves(tileLayout).find(({ kind, id }) => kind === 'slot' && !before.has(id));
  // The reflow lays the tile out (and clears full screen) after noting the size it returns to.
  return focus({ ...state, tileLayout, tilePickerSlotId: newSlot?.id }, id);
}

const sameBounds = (window: DesktopWindowState, rect: DesktopBounds & { x: number; y: number }) =>
  window.x === rect.x &&
  window.y === rect.y &&
  window.width === rect.width &&
  window.height === rect.height;

/**
 * Lays tiled windows out from the tree after any change. Tiles that closed or minimized leave it
 * (their neighbours take the space); a lone window without a free slot stops tiling. Windows out
 * of the tree return to the size they had before tiling.
 */
export function reflowTiles<T extends DesktopRouteState>(state: DesktopState<T>): DesktopState<T> {
  let layout: TileNode | undefined = state.tileLayout;
  for (const id of tiledWindowIds(layout)) {
    if (!state.windows.some((window) => window.id === id && isShown(window)))
      layout = removeLeaf(layout, id);
  }
  const leaves = tileLeaves(layout);
  const windowCount = leaves.filter(({ kind }) => kind === 'window').length;
  if (windowCount === 0 || (windowCount === 1 && leaves.length === 1)) layout = undefined;
  if (!layout && !state.tileLayout && !state.windows.some((window) => window.restoreBounds))
    return state;

  const rects = layoutTiles(layout, getDesktopRect(state.availableBounds)).leaves;
  // A covered desktop reports bounds too small to lay out: tiles keep their geometry until it's back.
  const covered = (state.availableBounds?.width ?? MIN_FIT_WIDTH) < MIN_FIT_WIDTH;
  const windows = state.windows.map((window): DesktopWindowState<T> => {
    const rect = rects.get(window.id);
    if (!rect) {
      if (!window.restoreBounds) return window;
      const { restoreBounds, ...rest } = window;
      return { ...rest, ...restoreBounds };
    }
    if (covered && window.restoreBounds) return window;
    if (window.restoreBounds && !window.isMaximized && sameBounds(window, rect)) return window;
    return {
      ...window,
      ...rect,
      isMaximized: false,
      restoreBounds: window.restoreBounds ?? {
        x: window.x,
        y: window.y,
        width: window.width,
        height: window.height,
        isMaximized: window.isMaximized,
      },
    };
  });
  // The offer goes away once a full-screen window covers the tiles: it would sit on top of it.
  const tilePickerSlotId =
    state.tilePickerSlotId &&
    rects.has(state.tilePickerSlotId) &&
    !getTileStacking({ tileLayout: layout, windows: state.windows }).covered
      ? state.tilePickerSlotId
      : undefined;
  if (
    layout === state.tileLayout &&
    tilePickerSlotId === state.tilePickerSlotId &&
    windows.every((window, i) => window === state.windows[i])
  )
    return state;
  return { ...state, tileLayout: layout, tilePickerSlotId, windows };
}
